import type { IFormValue } from "../modals/create-event";
import { type ConfigCustomEvent } from "../modals/custom-event";
import {
  formatDate,
  getDaysRangeFromDates,
  SelectTypeEnum,
  weekDaysLabels,
  type EventForDay,
} from "./utils";

interface IRule {
  week?: number;
  weekday?: number;
  business?: boolean | null;
  business_day_ordinal?: number;
  month?: number;
  weekend_ordinal?: number;
}

export const serializationEventData = (
  formValue: IFormValue,
  eventSelected?: EventForDay,
  configCustomEvent?: ConfigCustomEvent,
) => {
  const { title, event_type, date_range } = formValue;
  let { repeat_type } = formValue;
  let config_type = null;

  const recurring = !(repeat_type === "UNIQUE");

  let recurrence_meta = null;
  let rule: IRule | null = null;

  if (recurring && repeat_type === "WEEKLY") {
    recurrence_meta = {
      weekdays: [
        ...getDaysRangeFromDates(
          new Date(formatDate(date_range.start)),
          new Date(formatDate(date_range.end)),
        ),
      ],
    };
  }

  if (recurring && repeat_type === "CUSTOM") {
    repeat_type = configCustomEvent?.repeatType;

    if (configCustomEvent?.repeatType === SelectTypeEnum.DAILY_INTERVAL) {
      recurrence_meta = {
        interval: configCustomEvent?.quantity,
        weekdays: [
          ...getDaysRangeFromDates(
            new Date(formatDate(date_range.start)),
            new Date(formatDate(date_range.end)),
          ),
        ],
      };
    }

    if (configCustomEvent?.repeatType === SelectTypeEnum.WEEKLY_INTERVAL) {
      recurrence_meta = {
        interval: configCustomEvent?.quantity,
        weekdays: [Number(configCustomEvent.dayToStart)],
      };
    }

    if (configCustomEvent?.repeatType === SelectTypeEnum.MONTHLY_INTERVAL) {
      if (configCustomEvent.dateType === "date") {
        config_type = configCustomEvent.dateType;
        recurrence_meta = {
          interval: configCustomEvent?.quantity,
          days_of_month: configCustomEvent.daysSelected,
        };
      }

      if (configCustomEvent.dateType === "day") {
        config_type = configCustomEvent.dateType;

        if (configCustomEvent.repeatStartDay === "Dia") {
          recurrence_meta = {
            interval: configCustomEvent?.quantity,
            days_of_month: [Number(configCustomEvent.repeatStart)],
          };
        }

        if (configCustomEvent.repeatStartDay === "Dia de semana") {
          rule = {
            business_day_ordinal: Number(configCustomEvent.repeatStart),
          };
        }

        if (configCustomEvent.repeatStartDay === "Fin de semana") {
          rule = {
            weekend_ordinal: Number(configCustomEvent.repeatStart),
          };
        }

        if (weekDaysLabels.includes(configCustomEvent.repeatStartDay)) {
          rule = {
            week: Number(configCustomEvent.repeatStart),
            business: true,
            weekday:
              weekDaysLabels.findIndex(
                (d) => d === configCustomEvent.repeatStartDay,
              ) + 1,
          };
        }

        recurrence_meta = {
          ...recurrence_meta,
          interval: configCustomEvent?.quantity,
          rule,
        };
      }
    }

    if (configCustomEvent?.repeatType === SelectTypeEnum.YEARLY_INTERVAL) {
      if (configCustomEvent.dateType === "date") {
        config_type = configCustomEvent.dateType;
        recurrence_meta = {
          interval: configCustomEvent?.quantity,
          month: configCustomEvent.monthSelected,
          days_of_month: configCustomEvent.daysSelected,
        };
      }

      if (configCustomEvent.dateType === "day") {
        config_type = configCustomEvent.dateType;
        recurrence_meta = { month: configCustomEvent.monthSelected };

        if (configCustomEvent.repeatStartDay === "Dia") {
          recurrence_meta = {
            interval: configCustomEvent?.quantity,
            days_of_month: [Number(configCustomEvent.repeatStart)],
            ...recurrence_meta,
          };
        }

        if (configCustomEvent.repeatStartDay === "Dia de semana") {
          rule = {
            ...rule,
            business_day_ordinal: Number(configCustomEvent.repeatStart),
          };
        }

        if (configCustomEvent.repeatStartDay === "Fin de semana") {
          rule = {
            ...rule,
            weekend_ordinal: Number(configCustomEvent.repeatStart),
          };
        }

        if (weekDaysLabels.includes(configCustomEvent.repeatStartDay)) {
          rule = {
            ...rule,
            week: Number(configCustomEvent.repeatStart),
            weekday:
              weekDaysLabels.findIndex(
                (d) => d === configCustomEvent.repeatStartDay,
              ) + 1,
          };
        }

        recurrence_meta = {
          ...recurrence_meta,
          interval: configCustomEvent?.quantity,
          rule,
        };
      }
    }
  }

  return {
    id: eventSelected?.id ?? undefined,
    start_date: formatDate(date_range.start),
    end_date: formatDate(date_range.end),
    name: title ?? "",
    description: title ?? "",
    type: event_type ?? "",
    recurring,
    recurrence_type: repeat_type !== "UNIQUE" ? repeat_type : null,
    active: true,
    recurrence_meta,
    config_type,
  };
};
